import Vue from 'vue';
import { ErrorBag, FieldBag } from 'vee-validate';
import { IDictionary } from '@/entities/dictionary.interface';
import { IFormControl } from '@/entities/form.interface';
import { IResponseError, ResponseCode } from '@/services/rest/rest.service';
import { ICriterion } from '@/entities/criterion.interface';
import * as types from '@/store/types';
import { IPeriodShort } from '@/entities';
import { parse } from 'date-fns';
import {getDefaultDateRange, toStrPeriod} from '@/utils';

export abstract class FormComponent extends Vue {
    public loading: boolean = false;

    public fields: FieldBag; // already injected by vee-validate, here only for types

    public errors: ErrorBag; // already injected by vee-validate, here only for types

    public abstract formState: IDictionary<IFormControl>;

    get isValid(): boolean {
        return Object.keys(this.fields).reduce(
            (prev, key) => prev && this.fields[key].valid,
            true
        );
    }

    public processError(response: IResponseError) {
        this.loading = false;

        if (response.errorCode !== ResponseCode.ValidationError) {
            // it is not validation error
            return;
        }

        for (let field in response.data) {
            if (response.data.hasOwnProperty(field)) {
                response.data[field].map((msg) =>
                    this.errors.add({
                        field,
                        msg
                    })
                );
            }
        }
    }

    protected prepare(): IDictionary<any> {
        this.$validator.validateAll();

        return Object.keys(this.formState).reduce((prev, current) => {
            return {
                ...prev,
                [current]: this.formState[current].value
            };
        }, {});
    }

    protected toCriteria(): ICriterion[] {
        this.$validator.validateAll();

        return Object.keys(this.formState).reduce((prev, key) => {
            let control: IFormControl = this.formState[key];
            let value = control.value;
            if (control.toCriterion) {
                return (
                    (value !== null &&
                        value !== '' && [
                            ...prev,
                            control.toCriterion(control.value)
                        ]) ||
                    prev
                );
            }

            return (
                (value !== null &&
                    value !== '' && [
                        ...prev,
                        {
                            field: key,
                            value
                        }
                    ]) ||
                prev
            );
        }, []);
    }

    protected clear() {
        this.formState = Object.keys(this.formState).reduce((prev, current) => {
            return {
                ...prev,
                [current]: {
                    ...this.formState[current],
                    value: null
                }
            };
        }, {});
    }

    protected setValues(body: any) {
        Object.keys(this.formState).forEach((key: string) => {
            const bodyValue = body[key];
            if (bodyValue) {
                this.formState[key].value = bodyValue; // TODO prevent mutation
            }
        });

        this.$validator.validateAll();
    }

    protected getDefaultDateRange() {
        const v = this.$store.getters[types.USERDATA_GET_DEFAULT_PERIOD];
        return v ? v : getDefaultDateRange();
    }

    protected saveDateRange(dateRange) {
        const defaultDateRange = toStrPeriod(getDefaultDateRange());
        if (dateRange.start !== defaultDateRange.start || dateRange.end !== defaultDateRange.end) {
            this.$store.commit(types.USERDATA_SAVE_PERIOD, {
                start: parse(dateRange.start),
                end: parse(dateRange.end)
            });
        } else {
            this.$store.commit(types.USERDATA_RESET_PERIOD);
        }
    }
}
